import { InheritedConfig, IniInfo, PackageConfig, PackageConfigSection, SelectedPackage, UnconfiguredRules } from "system/types/interfaces";
import { PayloadAction } from "@reduxjs/toolkit";
import IPolicyEditorState from "system/types/store/IPolicyEditorState";
import { findLastIndexManual } from "system/libraries/utilities";
import policyEditorState from "./policy-editor-state";
import { isRowDirty } from "components/modules/policy-editor/utils/ruleSignature";

function syncRowDirty(rows: IniInfo[], affectedIndex: number) {
  const row = rows[affectedIndex];
  if (!row) return;
  row.pendingChanges = isRowDirty(row, rows);
}

// A package rule also counts as modified when the configuration under it changed.
function hasDirtyConfiguration(state: IPolicyEditorState, ruleName: string | undefined) {
  if (!ruleName) return false;
  const lines = state.configurationByPackage[ruleName];
  if (!lines?.length) return false;
  return lines.some((line) => isRowDirty(line, lines));
}

function syncModifiedRowsFor(state: IPolicyEditorState, ruleId: string | undefined) {
  if (!ruleId) return;
  const rule = state.rules.find((r) => r.id === ruleId);
  if (!rule) {
    state.modifiedRows = state.modifiedRows.filter((id) => id !== ruleId);
    return;
  }
  if (isRowDirty(rule, state.rules) || hasDirtyConfiguration(state, rule.name)) {
    if (!state.modifiedRows.includes(ruleId)) state.modifiedRows.push(ruleId);
  } else {
    state.modifiedRows = state.modifiedRows.filter((id) => id !== ruleId);
  }
}

type AllowedRuleFields = "inlineComment" | "value" | "delimiter";

const policyEditorReducers = {
  resetPolicyEditorState: () => ({
    ...policyEditorState,
    clientConfigModified: false,
  }),
  setLicenseFile: (state: IPolicyEditorState, action: PayloadAction<boolean>) => {
    state.licenseFileValid = action.payload;
  },
  setRules(state: IPolicyEditorState, action: PayloadAction<IniInfo[]>) {
    state.rules = action.payload.map(r => (r.id ? r : { ...r, id: crypto.randomUUID() }));
  },
  addRule(state: IPolicyEditorState, action: PayloadAction<{ index: number; rule: IniInfo }>) {
    const rule = action.payload.rule.id ? action.payload.rule : { ...action.payload.rule, id: crypto.randomUUID() };
    state.rules.splice(action.payload.index, 0, rule);
    syncModifiedRowsFor(state, rule.id);
  },
  updateRule(state: IPolicyEditorState, action: PayloadAction<{ index: number; updatedRule: IniInfo }>) {
    const { index, updatedRule } = action.payload;
    const prev = state.rules[index];
    state.rules[index] = {
      ...updatedRule,
      _originalSig: updatedRule._originalSig ?? prev?._originalSig,
      _originalIndex: updatedRule._originalIndex ?? prev?._originalIndex,
    };
    syncModifiedRowsFor(state, state.rules[index].id);
  },
  deleteRule(state: IPolicyEditorState, action: PayloadAction<number>) {
    state.rules[action.payload].isDeleted = true;
    syncModifiedRowsFor(state, state.rules[action.payload].id);
  },
  undoRuleDeletion: (state: IPolicyEditorState, action: PayloadAction<number>) => {
    state.rules[action.payload].isDeleted = false;
    syncModifiedRowsFor(state, state.rules[action.payload].id);
  },
  reorderRules(state: IPolicyEditorState, action: PayloadAction<{ oldIndex: number; newIndex: number }>) {
    const [movedRule] = state.rules.splice(action.payload.oldIndex, 1);
    state.rules.splice(action.payload.newIndex, 0, movedRule);
    syncModifiedRowsFor(state, movedRule.id);
  },
  setLastEditedIndex: (state: IPolicyEditorState, action: PayloadAction<number | null>) => {
    state.lastEditedIndex = action.payload;
  },
  updateModifiedRows(state: IPolicyEditorState, action: PayloadAction<string>) {
    if (!state.modifiedRows.includes(action.payload)) {
      state.modifiedRows.push(action.payload);
    }
  },
  removeModifiedRow(state: IPolicyEditorState, action: PayloadAction<string>) {
    state.modifiedRows = state.modifiedRows.filter((row) => row !== action.payload);
  },
  setPackages: (state: IPolicyEditorState, action: PayloadAction<PackageConfig[]>) => {
    state.packages = action.payload;
  },
  setGlobalCatalog: (state: IPolicyEditorState, action: PayloadAction<PackageConfig[]>) => {
    state.globalCatalog = action.payload;
  },
  setPendingReplicateChanges: (state: IPolicyEditorState, action: PayloadAction<{ additions: string[]; removals: string[] }>) => {
    const additions = action.payload.additions ?? [];
    const removals = action.payload.removals ?? [];
    state.pendingReplicateChanges = { additions, removals };
    state.initialPendingReplicateChanges = { additions: [...additions], removals: [...removals] };
  },
  addPendingReplicateAddition: (state: IPolicyEditorState, action: PayloadAction<string>) => {
    const name = action.payload?.trim();
    if (!name) return;
    const lower = name.toLowerCase();
    state.pendingReplicateChanges.removals = state.pendingReplicateChanges.removals.filter((p) => p.toLowerCase() !== lower);
    if (!state.pendingReplicateChanges.additions.some((p) => p.toLowerCase() === lower)) {
      state.pendingReplicateChanges.additions.push(name);
    }
  },
  removePendingReplicateAddition: (state: IPolicyEditorState, action: PayloadAction<string>) => {
    const lower = action.payload?.trim().toLowerCase();
    if (!lower) return;
    state.pendingReplicateChanges.additions = state.pendingReplicateChanges.additions.filter((p) => p.toLowerCase() !== lower);
  },
  addPendingReplicateRemoval: (state: IPolicyEditorState, action: PayloadAction<string>) => {
    const name = action.payload?.trim();
    if (!name) return;
    const lower = name.toLowerCase();
    state.pendingReplicateChanges.additions = state.pendingReplicateChanges.additions.filter((p) => p.toLowerCase() !== lower);
    if (!state.pendingReplicateChanges.removals.some((p) => p.toLowerCase() === lower)) {
      state.pendingReplicateChanges.removals.push(name);
    }
  },
  removePendingReplicateRemoval: (state: IPolicyEditorState, action: PayloadAction<string>) => {
    const lower = action.payload?.trim().toLowerCase();
    if (!lower) return;
    state.pendingReplicateChanges.removals = state.pendingReplicateChanges.removals.filter((p) => p.toLowerCase() !== lower);
  },

  //invalid rules
  setInvalidRules: (state: IPolicyEditorState, action: PayloadAction<{ index: number; rule: IniInfo; errors: string[] }[]>) => {
    state.invalidRules = action.payload;
  },
  removeInvalidRule: (state: IPolicyEditorState, action: PayloadAction<number>) => {
    state.invalidRules = state.invalidRules.filter((rule) => rule.index !== action.payload);
  },
  setInvalidConfigs: (state: IPolicyEditorState, action: PayloadAction<{ index: number; rule: IniInfo; errors: string[] }[]>) => {
    state.invalidConfigs = action.payload;
  },

  // selected package
  setSelectedPackage(state: IPolicyEditorState, action: PayloadAction<SelectedPackage>) {
    state.selectedPackage = action.payload;
  },
  updateSelectedPackageName(state: IPolicyEditorState, action: PayloadAction<string>) {
    if (state.selectedPackage) {
      state.selectedPackage.rule.name = action.payload;
    }
  },
  updateSelectedPackageActiveName(state: IPolicyEditorState, action: PayloadAction<string>) {
    if (state.selectedPackage) {
      state.selectedPackage.activePackageName = action.payload;
    }
  },

  updateSelectedPackageIsValid(state: IPolicyEditorState, action: PayloadAction<boolean>) {
    if (state.selectedPackage) {
      state.selectedPackage.isValid = action.payload;
    }
  },

  updateSelectedPackageField(state: IPolicyEditorState, action: PayloadAction<{ field: AllowedRuleFields; value: string }>) {
    if (!state.selectedPackage) return;

    state.selectedPackage.rule[action.payload.field] = action.payload.value;
  },

  // configuration tab
  setConfigurationForPackage(state: IPolicyEditorState, action: PayloadAction<PackageConfigSection[]>) {
    for (const section of action.payload) {
      if (section.lines.length === 0) {
        delete state.configurationByPackage[section.name];
      } else {
        state.configurationByPackage[section.name] = section.lines;
      }
    }
  },
  insertConfiguredItem(state: IPolicyEditorState, action: PayloadAction<{ packageId: string; item: IniInfo; position?: number }>) {
    const { packageId, item, position } = action.payload;
    const current = state.configurationByPackage[packageId] || [];
    const copy = [...current];

    if (item.name === "Pre-run Command") {
      const lastPreRunIndex = findLastIndexManual(copy, (line) => line.name === "Pre-run Command");

      if (lastPreRunIndex >= 0) {
        copy.splice(lastPreRunIndex + 1, 0, item);
      } else {
        copy.unshift(item);
      }
    } else if (item.name === "Post-run Command") {
      copy.push(item);
    } else {
      if (position !== undefined) {
        copy.splice(position, 0, item);
      } else {
        copy.push(item);
      }
    }

    item.pendingChanges = true;
    state.configurationByPackage[packageId] = copy;
  },
  updateConfiguredItem(state: IPolicyEditorState, action: PayloadAction<{ packageId: string; item: IniInfo }>) {
    const { packageId, item } = action.payload;
    const current = state.configurationByPackage[packageId] || [];
    const index = current.findIndex((c) => c.id === item.id);
    if (index >= 0) {
      item.pendingChanges = true;
      current[index] = { ...item };
    }
    state.configurationByPackage[packageId] = current;
  },
  deleteConfiguredItem(state: IPolicyEditorState, action: PayloadAction<{ packageId: string; id: string }>) {
    const { packageId, id } = action.payload;
    const current = state.configurationByPackage[packageId] || [];
    const index = current.findIndex((c) => c.id === id);
    if (index >= 0) {
      current[index] = { ...current[index], isDeleted: true };
    }
    state.configurationByPackage[packageId] = current;
  },
  undoDeleteConfiguredItem(state: IPolicyEditorState, action: PayloadAction<{ packageId: string; id: string }>) {
    const { packageId, id } = action.payload;
    const current = state.configurationByPackage[packageId] || [];
    const index = current.findIndex((c) => c.id === id);
    if (index >= 0) {
      current[index] = { ...current[index], isDeleted: false };
    }
    state.configurationByPackage[packageId] = current;
  },
  reorderConfiguredItems(state: IPolicyEditorState, action: PayloadAction<{ packageId: string; oldIndex: number; newIndex: number }>) {
    const { packageId, oldIndex, newIndex } = action.payload;
    const current = state.configurationByPackage[packageId] || [];
    const copy = [...current];
    const [moved] = copy.splice(oldIndex, 1);
    moved.pendingChanges = true;
    copy.splice(newIndex, 0, moved);
    state.configurationByPackage[packageId] = copy;
  },

  // clientConfig (general client configuration)
  setClientConfig(state: IPolicyEditorState, action: PayloadAction<IniInfo[]>) {
    state.clientConfig = action.payload;
    state.clientConfigModified = action.payload.some((rule) => rule.pendingChanges);
  },
  addClientRule(state: IPolicyEditorState, action: PayloadAction<{ item: IniInfo; position?: number }>) {
    const { item, position } = action.payload;
    const current = state.clientConfig;
    const copy = [...current];
    if (position !== undefined) {
      copy.splice(position, 0, item);
    } else {
      copy.push(item);
    }
    item.pendingChanges = true;
    state.clientConfig = copy;
    state.clientConfigModified = true;
  },
  updateClientRule(state: IPolicyEditorState, action: PayloadAction<{ item: IniInfo }>) {
    const { item } = action.payload;
    const current = state.clientConfig;
    const index = current.findIndex((c) => c.id === item.id);
    if (index >= 0) {
      item.pendingChanges = true;
      current[index] = { ...item };
    }
    state.clientConfig = current;
    state.clientConfigModified = true;
  },
  deleteClientRule(state: IPolicyEditorState, action: PayloadAction<{ id: string }>) {
    const { id } = action.payload;
    const current = state.clientConfig;
    const index = current.findIndex((c) => c.id === id);
    if (index >= 0) {
      current[index] = { ...current[index], isDeleted: true };
    }
    state.clientConfig = current;
    state.clientConfigModified = true;
  },
  undoDeleteClientRule(state: IPolicyEditorState, action: PayloadAction<{ id: string }>) {
    const { id } = action.payload;
    const current = state.clientConfig;
    const index = current.findIndex((c) => c.id === id);
    if (index >= 0) {
      current[index] = { ...current[index], isDeleted: false };
    }
    state.clientConfig = current;
    state.clientConfigModified = true;
  },
  reorderClientRules(state: IPolicyEditorState, action: PayloadAction<{ oldIndex: number; newIndex: number }>) {
    const { oldIndex, newIndex } = action.payload;
    const current = state.clientConfig;
    const copy = [...current];
    const [moved] = copy.splice(oldIndex, 1);
    moved.pendingChanges = true;
    copy.splice(newIndex, 0, moved);
    state.clientConfig = copy;
    state.clientConfigModified = true;
  },

  // Inherited Configurations
  addInheritedConfiguration: (state: IPolicyEditorState, action: PayloadAction<{ regex: string; matchingPackages: string[] }>) => {
    const id = state.inheritedConfigurations.map((c) => c.id).sort((a, b) => b - a);
    state.inheritedConfigurations.push({
      id: id[0] + 1,
      regex: action.payload.regex,
      matchingPackages: action.payload.matchingPackages,
      configurations: [],
      notConfiguredRules: [],
      pendingChanges: true,
    });
  },
  setInheritedConfigurations: (state: IPolicyEditorState, action: PayloadAction<InheritedConfig[]>) => {
    state.inheritedConfigurations = action.payload;
  },
  updateInheritedNotConfigured: (state: IPolicyEditorState, action: PayloadAction<{ id: number; notConfigured: UnconfiguredRules[] }>) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (config) {
      config.notConfiguredRules = action.payload.notConfigured;
      config.configurations = config.configurations.map((c) => {
        const defaultConfig = action.payload.notConfigured.find((r) => r.name === c.name);
        return defaultConfig ? { ...c, type: defaultConfig.type, valueShouldBeLogged: defaultConfig.valueShouldBeLogged } : c;
      });
    }
  },
  addInheritedConfigurationItem: (state: IPolicyEditorState, action: PayloadAction<{ id: number; item: IniInfo; position?: number }>) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (config) {
      const items = [...config.configurations];
      if (action.payload.position !== undefined) {
        items.splice(action.payload.position, 0, { ...action.payload.item, pendingChanges: true });
      } else {
        items.push({ ...action.payload.item, pendingChanges: true });
      }
      config.configurations = items;
    }
  },
  updateInheritedConfigurationItem: (state: IPolicyEditorState, action: PayloadAction<{ id: number; item: IniInfo }>) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (config) {
      const index = config.configurations.findIndex((c) => c.id === action.payload.item.id);
      if (index >= 0) {
        config.configurations[index] = { ...action.payload.item, pendingChanges: true };
      }
    }
  },
  deleteInheritedConfigurationItem: (state: IPolicyEditorState, action: PayloadAction<{ id: number; itemId: string }>) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (config) {
      const index = config.configurations.findIndex((c) => c.id === action.payload.itemId);
      if (index >= 0) {
        config.configurations[index] = { ...config.configurations[index], isDeleted: true, pendingChanges: true };
      }
    }
  },
  undoDeleteInheritedConfigurationItem: (state: IPolicyEditorState, action: PayloadAction<{ id: number; itemId: string }>) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (config) {
      const index = config.configurations.findIndex((c) => c.id === action.payload.itemId);
      if (index >= 0) {
        config.configurations[index] = { ...config.configurations[index], isDeleted: false, pendingChanges: true };
      }
    }
  },
  reorderInheritedConfigurationItems: (
    state: IPolicyEditorState,
    action: PayloadAction<{ id: number; oldIndex: number; newIndex: number }>
  ) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (config) {
      const items = [...config.configurations];
      const [moved] = items.splice(action.payload.oldIndex, 1);
      items.splice(action.payload.newIndex, 0, { ...moved, pendingChanges: true });
      config.configurations = items;
    }
  },
  updateInheritedRegex: (
    state: IPolicyEditorState,
    action: PayloadAction<{ id: number; newRegex: string; newMatchingPackages: string[] }>
  ) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (config) {
      config.regex = action.payload.newRegex;
      config.matchingPackages = action.payload.newMatchingPackages;
      config.pendingChanges = true;
    }
  },

  removeInheritedConfiguration: (state: IPolicyEditorState, action: PayloadAction<{ id: number }>) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (config) {
      config.isDeleted = true;
      config.pendingChanges = true;
    }
  },

  restoreInheritedConfiguration: (state: IPolicyEditorState, action: PayloadAction<{ id: number }>) => {
    const config = state.inheritedConfigurations.find((c) => c.id === action.payload.id);
    if (!config) return;
    config.isDeleted = false;
    config.pendingChanges = false;
  },

  //sites and subnets
  setSitesAndSubnets: (state: IPolicyEditorState, action: PayloadAction<IniInfo[]>) => {
    state.sitesAndSubnets = action.payload;
  },
  addSiteSubnet: (state: IPolicyEditorState, action: PayloadAction<{ item: IniInfo; index: number }>) => {
    const { item, index } = action.payload;
    const newItem = { ...item };
    if (index !== undefined && index >= 0 && index <= state.sitesAndSubnets.length) {
      state.sitesAndSubnets.splice(index, 0, newItem);
    } else {
      state.sitesAndSubnets.push(newItem);
    }
    state.sitesAndSubnets = state.sitesAndSubnets.sort((a, b) => a.name.localeCompare(b.name));
    const newIdx = state.sitesAndSubnets.findIndex((s) => s.id === newItem.id);
    syncRowDirty(state.sitesAndSubnets, newIdx);
  },
  updateSiteSubnet: (state: IPolicyEditorState, action: PayloadAction<{ updatedItem: IniInfo }>) => {
    const index = state.sitesAndSubnets.findIndex((item) => item.id === action.payload.updatedItem.id);
    if (index >= 0) {
      const prev = state.sitesAndSubnets[index];
      state.sitesAndSubnets[index] = {
        ...action.payload.updatedItem,
        _originalSig: action.payload.updatedItem._originalSig ?? prev?._originalSig,
        _originalIndex: action.payload.updatedItem._originalIndex ?? prev?._originalIndex,
      };
      syncRowDirty(state.sitesAndSubnets, index);
    }
  },
  deleteSiteSubnet: (state: IPolicyEditorState, action: PayloadAction<string>) => {
    const index = state.sitesAndSubnets.findIndex((item) => item.name === action.payload);
    if (index >= 0) {
      state.sitesAndSubnets[index].isDeleted = true;
      syncRowDirty(state.sitesAndSubnets, index);
    }
  },
  undoDeleteSiteSubnet: (state: IPolicyEditorState, action: PayloadAction<string>) => {
    const index = state.sitesAndSubnets.findIndex((item) => item.name === action.payload);
    if (index >= 0) {
      state.sitesAndSubnets[index].isDeleted = false;
      syncRowDirty(state.sitesAndSubnets, index);
    }
  },

  //Configurations section
  setSystemConfigurations: (state: IPolicyEditorState, action: PayloadAction<IniInfo[]>) => {
    state.systemConfigurations = action.payload;
  },
  addSystemConfig: (state: IPolicyEditorState, action: PayloadAction<{ index: number; config: IniInfo }>) => {
    state.systemConfigurations.splice(action.payload.index, 0, { ...action.payload.config });
    syncRowDirty(state.systemConfigurations, action.payload.index);
  },
  updateSystemConfig: (state: IPolicyEditorState, action: PayloadAction<{ index: number; updatedConfig: IniInfo }>) => {
    const { index, updatedConfig } = action.payload;
    const prev = state.systemConfigurations[index];
    state.systemConfigurations[index] = {
      ...updatedConfig,
      _originalSig: updatedConfig._originalSig ?? prev?._originalSig,
      _originalIndex: updatedConfig._originalIndex ?? prev?._originalIndex,
    };
    syncRowDirty(state.systemConfigurations, index);
  },
  deleteSystemConfig: (state: IPolicyEditorState, action: PayloadAction<number>) => {
    state.systemConfigurations[action.payload].isDeleted = true;
    syncRowDirty(state.systemConfigurations, action.payload);
  },
  undoDeleteSystemConfig: (state: IPolicyEditorState, action: PayloadAction<number>) => {
    state.systemConfigurations[action.payload].isDeleted = false;
    syncRowDirty(state.systemConfigurations, action.payload);
  },
  reorderSystemConfigs: (state: IPolicyEditorState, action: PayloadAction<{ oldIndex: number; newIndex: number }>) => {
    const [movedConfig] = state.systemConfigurations.splice(action.payload.oldIndex, 1);
    state.systemConfigurations.splice(action.payload.newIndex, 0, movedConfig);
    // Enforce "Standard" last
    const standardIndex = state.systemConfigurations.findIndex((c) => c.name === "Standard");
    if (standardIndex >= 0 && standardIndex !== state.systemConfigurations.length - 1) {
      const [standard] = state.systemConfigurations.splice(standardIndex, 1);
      state.systemConfigurations.push(standard);
    }
    const newIdx = state.systemConfigurations.findIndex((c) => c.id === movedConfig.id);
    syncRowDirty(state.systemConfigurations, newIdx);
  },

  //Active directory domains
  setActiveDirectoryDomains: (state: IPolicyEditorState, action: PayloadAction<IniInfo[]>) => {
    state.activeDirectoryDomains = action.payload;
  },
  addADDomain: (state: IPolicyEditorState, action: PayloadAction<{ index: number; config: IniInfo }>) => {
    const { index, config } = action.payload;
    state.activeDirectoryDomains.splice(index, 0, { ...config });
    syncRowDirty(state.activeDirectoryDomains, index);
  },
  updateADDomain: (state: IPolicyEditorState, action: PayloadAction<{ index: number; updatedConfig: IniInfo }>) => {
    const { index, updatedConfig } = action.payload;
    const prev = state.activeDirectoryDomains[index];
    state.activeDirectoryDomains[index] = {
      ...updatedConfig,
      _originalSig: updatedConfig._originalSig ?? prev?._originalSig,
      _originalIndex: updatedConfig._originalIndex ?? prev?._originalIndex,
    };
    syncRowDirty(state.activeDirectoryDomains, index);
  },
  deleteADDomain: (state: IPolicyEditorState, action: PayloadAction<number>) => {
    state.activeDirectoryDomains[action.payload].isDeleted = true;
    syncRowDirty(state.activeDirectoryDomains, action.payload);
  },
  undoDeleteADDomain: (state: IPolicyEditorState, action: PayloadAction<number>) => {
    state.activeDirectoryDomains[action.payload].isDeleted = false;
    syncRowDirty(state.activeDirectoryDomains, action.payload);
  },
  reorderADDomains: (state: IPolicyEditorState, action: PayloadAction<{ oldIndex: number; newIndex: number }>) => {
    const [moved] = state.activeDirectoryDomains.splice(action.payload.oldIndex, 1);
    state.activeDirectoryDomains.splice(action.payload.newIndex, 0, moved);
    syncRowDirty(state.activeDirectoryDomains, action.payload.newIndex);
  },

  // --- Active Directory OUs ---
  setActiveDirectoryOUs: (state: IPolicyEditorState, action: PayloadAction<IniInfo[]>) => {
    state.activeDirectoryOUs = action.payload;
  },
  setCurrentVersionId: (state: IPolicyEditorState, action: PayloadAction<string | null>) => {
    state.currentVersionId = action.payload;
  },
  setPendingConfigSections: (state: IPolicyEditorState, action: PayloadAction<PackageConfigSection[] | null>) => {
    state.pendingConfigSections = action.payload;
  },
  addADOU: (state: IPolicyEditorState, action: PayloadAction<{ index: number; config: IniInfo }>) => {
    const { index, config } = action.payload;
    state.activeDirectoryOUs.splice(index, 0, { ...config });
    syncRowDirty(state.activeDirectoryOUs, index);
  },
  updateADOU: (state: IPolicyEditorState, action: PayloadAction<{ index: number; updatedConfig: IniInfo }>) => {
    const { index, updatedConfig } = action.payload;
    const prev = state.activeDirectoryOUs[index];
    state.activeDirectoryOUs[index] = {
      ...updatedConfig,
      _originalSig: updatedConfig._originalSig ?? prev?._originalSig,
      _originalIndex: updatedConfig._originalIndex ?? prev?._originalIndex,
    };
    syncRowDirty(state.activeDirectoryOUs, index);
  },
  deleteADOU: (state: IPolicyEditorState, action: PayloadAction<number>) => {
    state.activeDirectoryOUs[action.payload].isDeleted = true;
    syncRowDirty(state.activeDirectoryOUs, action.payload);
  },
  undoDeleteADOU: (state: IPolicyEditorState, action: PayloadAction<number>) => {
    state.activeDirectoryOUs[action.payload].isDeleted = false;
    syncRowDirty(state.activeDirectoryOUs, action.payload);
  },
  reorderADOUs: (state: IPolicyEditorState, action: PayloadAction<{ oldIndex: number; newIndex: number }>) => {
    const [moved] = state.activeDirectoryOUs.splice(action.payload.oldIndex, 1);
    state.activeDirectoryOUs.splice(action.payload.newIndex, 0, moved);
    syncRowDirty(state.activeDirectoryOUs, action.payload.newIndex);
  },
};

export default policyEditorReducers;
